import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";

import { useLocale } from "@/hooks/useLocale";

/**
 * Language picker for the top bar.
 *
 * Options come from the APP_LOCALES registry, so adding a language needs no
 * change here. Direction and `<html lang>` are handled centrally by src/i18n's
 * `languageChanged` listener — not by this component — so the choice also
 * applies on a cold load, before the switcher mounts.
 */
export const LanguageSwitcher = ({ className }: { className?: string }) => {
  const { t } = useTranslation("common");
  const { code, availableLocales, changeLanguage } = useLocale();

  return (
    <Select value={code} onValueChange={changeLanguage}>
      <SelectTrigger
        aria-label={t("language.change")}
        className={`max-w-32 h-9 border-none p-0! w-fit! ${className ?? ""}`}
      >
        <SelectValue>
          <div className="flex items-center gap-2">
            <Flag locale={code} />
            <span className="hidden md:block font-medium text-sm">
              {availableLocales.find((locale) => locale.code === code)?.label}
            </span>
          </div>
        </SelectValue>
      </SelectTrigger>

      <SelectContent align="end" className="min-w-28 p-0!">
        {availableLocales.map(({ code: localeCode, label }) => (
          <SelectItem
            key={localeCode}
            value={localeCode}
            className="cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <Flag locale={localeCode} />
              <span>{label}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Note: Add a flag svg named after the locale in /public/locales/flags.
// SVGs are from https://nucleoapp.com/svg-flag-icons
const Flag = ({ locale }: { locale: string }) => (
  <img
    alt=""
    aria-hidden="true"
    src={`/locales/flags/${locale}.svg`}
    className="size-5 no-flip"
  />
);
